import { useEffect, useReducer, useRef, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import Popup from "@common/components/Popup";
import MaterialIcon from "@common/components/MaterialIcon";
import AuthMenu from "../auth/components/AuthMenu";
import Library from "../emulator/components/Library";
import SavePanel from "../emulator/components/SavePanel";
import Screen from "../emulator/components/Screen";
import Touchpad from "../emulator/components/Touchpad";
import { EJS_PLAYER_ID, EJS_SYSTEM_LABEL, type EjsSystem, ejsProgress, ejsRoms, ejsSystemOf, rememberedSystem } from "../emulator/ejs";
import { pendingName as pendingFromUrl, takeRom } from "../emulator/pending";
import { openingStep, type OpeningStep } from "../emulator/openingSteps";
import { displayName } from "../emulator/saves";
import Cover from "../library/Cover";
import { FAST_FORWARD_OPTIONS, useEmulator, type Emulator } from "../emulator/useEmulator";
import { usePlaytime } from "../emulator/usePlaytime";
import { useSession } from "@jkast90/kast-common/auth";
import { GuidePanel } from "../guides/components/GuidePanel";
import KeyHelp from "../emulator/components/KeyHelp";
import GameCard from "../emulator/components/GameCard";
import ThemeControl from "@common/components/ThemeControl";
import Dropdown from "@common/components/Dropdown";
import {
  loadMovement,
  loadN64Controls,
  loadShoulders,
  MOVEMENT_OPTIONS,
  N64_DEFAULT,
  N64_MOVE_OPTIONS,
  saveMovement,
  saveN64Controls,
  saveShoulders,
  type Movement,
  type N64Controls,
} from "../emulator/movement";
import N64Move from "../emulator/components/N64Move";
import type { CheatId } from "../emulator/cheats";

type QuickSaveState = "idle" | "saving" | "synced" | "local" | "failed";

const SPEED_OPTIONS = FAST_FORWARD_OPTIONS.map((m) => ({ value: String(m), label: `${m * 100}%` }));

/** How long the button shows the outcome before going back to the save icon. */
const QUICK_SAVE_SHOWN_MS = 2500;

// The quick save button tells the player how it went on the button itself:
// a spinner while saving, a green tick once saved (with "cloud" when the
// upload made it), red when it didn't.
function QuickSaveButton({ emu, className = "" }: { emu: Emulator; className?: string }) {
  const [state, setState] = useState<QuickSaveState>("idle");
  const timer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(timer.current), []);
  const save = async () => {
    if (state === "saving") return;
    window.clearTimeout(timer.current);
    setState("saving");
    const result = await emu.quickSave();
    setState(result);
    timer.current = window.setTimeout(() => setState("idle"), QUICK_SAVE_SHOWN_MS);
  };
  const tip =
    state === "saving"
      ? "Saving…"
      : state === "synced"
        ? "Saved and uploaded"
        : state === "local"
          ? "Saved here; not uploaded"
          : state === "failed"
            ? "Could not save"
            : "Quick save: write the slot and upload it now";
  const done = state === "synced" || state === "local";
  return (
    <button
      type="button"
      className={`btn icon-btn${className ? ` ${className}` : ""}${done ? " saved" : ""}${state === "failed" ? " save-failed" : ""}`}
      aria-label={tip}
      data-tip={tip}
      aria-live="polite"
      onClick={() => void save()}
    >
      {state === "saving" ? (
        <span className="spinner" aria-hidden="true" />
      ) : (
        <MaterialIcon name={state === "synced" ? "cloud_done" : state === "local" ? "check" : state === "failed" ? "close" : "save"} />
      )}
    </button>
  );
}

export default function PlayPage() {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const emu = useEmulator(canvasRef);
  const isAuthenticated = useSession().status === "signedIn";
  usePlaytime(emu, isAuthenticated);
  // N64, on EmulatorJS.
  const onEjs = emu.engine === "ejs";
  // Minimized Library and Saves widgets render into this dock via portals.
  const [dock, setDock] = useState<HTMLDivElement | null>(null);
  // The guide panel can be put away with its own button; remembered. On a
  // small or touch screen it floats over the game, so it starts closed.
  const [guideOpen, setGuideOpen] = useState(() => {
    const small = window.matchMedia("(max-width: 760px), (pointer: coarse)").matches;
    try {
      const saved = localStorage.getItem("guide-open");
      return saved === null ? !small : saved === "true";
    } catch {
      return !small;
    }
  });
  // How the on-screen pad moves: the player's pick, remembered.
  const [movement, setMovementState] = useState<Movement>(loadMovement);
  const setMovement = (next: Movement) => {
    setMovementState(next);
    saveMovement(next);
  };
  // L and R, shown or not per kind of cartridge (Game Boy and Color have
  // none, so theirs starts hidden).
  const [, shouldersChanged] = useReducer((n: number) => n + 1, 0);
  const cartridge = emu.cartridge === "gba" ? "gba" : "gb";
  // Hidden until the cartridge is known (a zip is read for it).
  const shoulders = emu.cartridge !== null && loadShoulders(cartridge);
  const setShoulders = (shown: boolean) => {
    saveShoulders(cartridge, shown);
    shouldersChanged();
  };
  // An N64 game's movement controls, chosen per game (movement.ts).
  const [, n64Changed] = useReducer((n: number) => n + 1, 0);
  const n64 = emu.current ? loadN64Controls(emu.current) : N64_DEFAULT;
  const setN64 = (patch: Partial<N64Controls>) => {
    if (!emu.current) return;
    saveN64Controls(emu.current, { ...n64, ...patch });
    n64Changed();
  };
  const [applyingCheats, setApplyingCheats] = useState(false);
  const applyCheats = async (ids: CheatId[]) => {
    setApplyingCheats(true);
    try {
      await emu.setCheats(ids);
    } finally {
      setApplyingCheats(false);
    }
  };
  // Touch devices get the on-screen pad, so the keyboard reference is noise.
  const [touchOnly, setTouchOnly] = useState(() => window.matchMedia("(pointer: coarse)").matches);
  useEffect(() => {
    const query = window.matchMedia("(pointer: coarse)");
    const onChange = () => setTouchOnly(query.matches);
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, []);
  // Fullscreen plus a landscape lock, which is what switches touch devices
  // into the landscape layout. Not offered where fullscreen is unavailable
  // (iPhone Safari), where rotating the phone does the same job.
  const [fullscreen, setFullscreen] = useState(() => !!document.fullscreenElement);
  useEffect(() => {
    const onChange = () => setFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);
  const toggleFullscreen = async () => {
    try {
      if (document.fullscreenElement) {
        screen.orientation?.unlock?.();
        await document.exitFullscreen();
      } else {
        await document.documentElement.requestFullscreen();
        await (screen.orientation as ScreenOrientation & { lock?: (o: string) => Promise<void> })?.lock?.("landscape");
      }
    } catch {
      // Orientation lock is refused on some browsers; fullscreen alone still helps.
    }
  };
  // iPhones allow fullscreen for videos only, in every browser. Opened from
  // the home screen the site gets its own window without the address bar,
  // so there the button explains how to get that instead.
  const standalone =
    window.matchMedia("(display-mode: standalone)").matches || (navigator as Navigator & { standalone?: boolean }).standalone === true;
  const [homeScreenTip, setHomeScreenTip] = useState(false);
  // Settings and the keys each open from their own button, one at a time.
  // Both start closed on every load.
  const [controlsOpen, setControlsOpenState] = useState(false);
  const [keysOpen, setKeysOpenState] = useState(false);
  const setControlsOpen = (open: boolean) => {
    setControlsOpenState(open);
    if (open) setKeysOpenState(false);
  };
  const setKeysOpen = (open: boolean) => {
    setKeysOpenState(open);
    if (open) setControlsOpenState(false);
  };
  // EmulatorJS pops its menu bar on any mouse movement and leaves it
  // up while paused; it stays hidden unless pinned with the menu button or
  // the mouse rests on the bottom of the game for a second.
  const [ejsMenuPinned, setEjsMenuPinned] = useState(false);
  const [ejsMenuHover, setEjsMenuHover] = useState(false);
  const hoverTimer = useRef<number | undefined>(undefined);
  const ejsMenuOpen = ejsMenuPinned || ejsMenuHover;
  const { setEjsMenu } = emu;
  useEffect(() => {
    if (onEjs) setEjsMenu(ejsMenuOpen);
  }, [onEjs, ejsMenuOpen, setEjsMenu]);
  const onEjsPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.pointerType !== "mouse") return;
    const rect = e.currentTarget.getBoundingClientRect();
    const nearBottom = e.clientY > rect.bottom - rect.height * 0.25;
    if (!nearBottom) {
      window.clearTimeout(hoverTimer.current);
      hoverTimer.current = undefined;
      setEjsMenuHover(false);
    } else if (hoverTimer.current === undefined) {
      hoverTimer.current = window.setTimeout(() => setEjsMenuHover(true), 1000);
    }
  };
  const onEjsPointerLeave = () => {
    window.clearTimeout(hoverTimer.current);
    hoverTimer.current = undefined;
    setEjsMenuHover(false);
  };
  const [showLibraryModal, setShowLibraryModal] = useState(false);
  // Picking a game (here or by any other route) closes the picker.
  const currentGame = emu.current;
  useEffect(() => {
    if (currentGame) setShowLibraryModal(false);
  }, [currentGame]);

  useEffect(() => {
    try {
      localStorage.setItem("guide-open", String(guideOpen));
    } catch {
      // Ignore storage errors
    }
  }, [guideOpen]);

  // On a phone in portrait the guide sits under the pad, off the screen:
  // opening it from the button brings it up.
  const guideSide = useRef<HTMLElement | null>(null);
  const openGuide = () => {
    const open = !guideOpen;
    setGuideOpen(open);
    if (open && window.matchMedia("(orientation: portrait) and ((max-width: 760px) or (pointer: coarse))").matches) {
      window.setTimeout(() => guideSide.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
    }
  };

  // A ROM picked on the home page is imported once the core is up; addRom
  // also starts it. Its name is kept so the guard below does not send the
  // page home while the import runs (the game param is written with
  // replaceState, which the router does not observe).
  const [pendingName] = useState(pendingFromUrl);
  const { status, addRom } = emu;
  useEffect(() => {
    if (status !== "ready") return;
    let live = true;
    void takeRom().then((file) => {
      if (file && live) void addRom(file);
    });
    return () => {
      live = false;
    };
  }, [status, addRom]);

  // EmulatorJS's own progress ("Download Game Data 45%") while it starts,
  // read from its hidden UI so the screen can show it under the cover.
  const [ejsProgressText, setEjsProgressText] = useState<string | null>(null);
  useEffect(() => {
    if (!emu.starting) {
      setEjsProgressText(null);
      return;
    }
    const timer = window.setInterval(() => setEjsProgressText(ejsProgress()), 300);
    return () => window.clearInterval(timer);
  }, [emu.starting]);

  // What's opening, from the moment the page loads until the game runs:
  // EmulatorJS reports its system once it starts; before that only the name
  // (from the URL, or a file handed over from the home page) is known.
  const urlGame = params.get("game");
  const openingName = !emu.current && !emu.error && !emu.fetching ? (urlGame ?? pendingName) : null;
  // The console of a game already in EmulatorJS's store, looked up up front so
  // the step count is right from the first step.
  const [knownSystem, setKnownSystem] = useState<{ name: string; system: EjsSystem | null } | null>(null);
  useEffect(() => {
    if (!openingName || knownSystem?.name === openingName) return;
    let live = true;
    const remembered = rememberedSystem(openingName);
    (remembered
      ? Promise.resolve(remembered)
      : ejsRoms.get(openingName).then((blob) => (blob ? ejsSystemOf(openingName, blob) : null))
    ).then(
      (system) => live && setKnownSystem({ name: openingName, system }),
      () => {},
    );
    return () => {
      live = false;
    };
  }, [openingName, knownSystem]);
  const opening =
    emu.starting ??
    (openingName
      ? { name: openingName, system: knownSystem?.name === openingName ? knownSystem.system : null }
      : null);

  // Which step of the opening sequence this is. It only moves forward:
  // EmulatorJS's line can briefly blank between stages.
  const sawGameDownload = useRef(false);
  const furthest = useRef<OpeningStep | null>(null);
  if (!opening) {
    sawGameDownload.current = false;
    furthest.current = null;
  }
  if (ejsProgressText?.startsWith("Download Game Data")) sawGameDownload.current = true;
  let step: OpeningStep | null = null;
  if (opening) {
    const now = openingStep({
      system: opening.system,
      booting: emu.status === "booting",
      ejsStarted: !!emu.starting,
      ejsText: ejsProgressText,
      sawGameDownload: sawGameDownload.current,
    });
    const before = furthest.current;
    step = before && before.total === now.total && before.number > now.number ? before : now;
    furthest.current = step;
  }

  // Game selection lives on the home page; the player only exists for a
  // game.
  if (!params.has("game") && !pendingName && !emu.current) return <Navigate to="/" replace />;

  return (
    <div className="app play">
      <header className="topbar">
        {/* A plain link on purpose: the core is a page singleton, so leaving the
            player is a full load. */}
        <a href="/" className="brand" title="Menu">
          <img src="/icons/favicon-64.png" alt="CloudBoy" className="brand-icon" />
          <strong>CloudBoy</strong>
          {!emu.current && emu.version && <span className="muted small">{emu.version}</span>}
        </a>
        <AuthMenu />
      </header>

      <main className={`layout${onEjs ? " ejs-game" : ""}${onEjs && n64.stick !== "native" ? " n64-no-own-stick" : ""}${onEjs && n64.dpad !== "native" ? " n64-no-own-dpad" : ""}`}>
        <div>
          {emu.status === "unsupported" && (
            <div className="notice error">
              This page isn't cross-origin isolated, so the emulator's threads can't start. The server must send
              <code> Cross-Origin-Opener-Policy: same-origin</code> and
              <code> Cross-Origin-Embedder-Policy: require-corp</code>.
            </div>
          )}
          {emu.status === "booting" && !opening && <div className="notice">Loading emulator core…</div>}
          {emu.error && <div className="notice error">{emu.error}</div>}
          {emu.notice && <div className="notice">{emu.notice}</div>}
          {emu.fetching && (
            <div className="notice">
              Downloading {displayName(emu.fetching.name)}… {Math.round(emu.fetching.fraction * 100)}%
            </div>
          )}

          {/* Until the game runs, the screen shows its cover and what's
              happening; paused, the cover again with a way back in. */}
          <Screen
            ref={canvasRef}
            hidden={onEjs}
            overlay={
              opening ? (
                <div className="opening" role="status" aria-live="polite">
                  <Cover name={opening.name} className="screen-cover" />
                  <div className="opening-line">
                    <span className="spinner" aria-hidden="true" />
                    <div className="opening-text">
                      <strong>{displayName(opening.name)}</strong>
                      {step && (
                        <span className="muted small">
                          Step {step.number} of {step.total}: {step.label}
                          {step.detail ? ` · ${step.detail}` : "…"}
                        </span>
                      )}
                      {opening.system && (
                        <span className="muted small">
                          {EJS_SYSTEM_LABEL[opening.system]}: big games can take a minute the first time.
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                emu.paused &&
                emu.current && (
                  <div className="opening" role="status" aria-live="polite">
                    <Cover name={emu.current} className="screen-cover" />
                    <div className="opening-line">
                      <MaterialIcon name="pause" />
                      <div className="opening-text">
                        <strong>{displayName(emu.current)}</strong>
                        <span className="muted small">
                          Paused{emu.idleSince ? ` since ${emu.idleSince.toLocaleTimeString([], { timeStyle: "short" })}` : ""}
                        </span>
                        {emu.graphicsLost && (
                          <span className="muted small">The graphics were reset while you were away. Resume restarts the game from where you were.</span>
                        )}
                      </div>
                    </div>
                    <div className="btn-group opening-actions">
                      <button type="button" className="btn primary" onClick={emu.togglePause}>
                        <MaterialIcon name="play_arrow" /> Resume
                      </button>
                      <button
                        type="button"
                        className="btn"
                        onClick={() => void emu.resetConsole()}
                        data-tip="As the console's reset button: two-in-one carts go back to their menu"
                      >
                        <MaterialIcon name="refresh" /> Reset console
                      </button>
                    </div>
                  </div>
                )
              )
            }
          />
          <div
            className={`ejs-wrap${ejsMenuOpen ? " menu-open" : ""}`}
            hidden={!onEjs}
            onPointerMove={onEjsPointerMove}
            onPointerLeave={onEjsPointerLeave}
          >
            <div id={EJS_PLAYER_ID} className="ejs-player" />
            {touchOnly && onEjs && emu.current && (
              <N64Move
                controls={n64}
                input={emu.ejsInput}
                active={!emu.paused}
                turboLocked={emu.fastForwardOn}
                onTurbo={emu.holdFastForward}
                onTurboLock={emu.toggleFastForward}
              />
            )}
          </div>

          {emu.current && (
            <>
            <button
              type="button"
              className="btn icon-btn controls-toggle"
              aria-expanded={controlsOpen}
              aria-label={controlsOpen ? "Hide game controls" : "Show game controls"}
              data-tip="Game, slots and speed"
              onClick={() => setControlsOpen(!controlsOpen)}
            >
              <MaterialIcon name="menu" />
            </button>
            <QuickSaveButton emu={emu} className="save-side" />
            <button
              type="button"
              className={`btn icon-btn pause-side${emu.paused ? " primary" : ""}`}
              aria-pressed={emu.paused}
              aria-label={emu.paused ? "Resume" : "Pause"}
              data-tip={emu.paused ? "Resume" : "Pause"}
              onClick={emu.togglePause}
            >
              <MaterialIcon name={emu.paused ? "play_arrow" : "pause"} />
            </button>
            <button
              type="button"
              className="btn icon-btn mute-side"
              aria-pressed={emu.muted}
              aria-label={emu.muted ? "Unmute" : "Mute"}
              data-tip={emu.muted ? "Unmute" : "Mute"}
              onClick={emu.toggleMute}
            >
              <MaterialIcon name={emu.muted ? "volume_off" : "volume_up"} />
            </button>
            <button
              type="button"
              className={`btn icon-btn guide-side${guideOpen ? " primary" : ""}`}
              aria-pressed={guideOpen}
              aria-label={guideOpen ? "Hide the guide" : "Show the guide"}
              data-tip={guideOpen ? "Hide the guide" : "Show the guide"}
              onClick={openGuide}
            >
              <MaterialIcon name="menu_book" />
            </button>
            {/* Touch screens get the on-screen pad, so no keyboard there. */}
            {!touchOnly && (
              <button
                type="button"
                className={`btn icon-btn keys-side${keysOpen ? " primary" : ""}`}
                aria-expanded={keysOpen}
                aria-label={keysOpen ? "Hide the keys" : "Show the keys"}
                data-tip="Keys and controller profile"
                onClick={() => setKeysOpen(!keysOpen)}
              >
                <MaterialIcon name="keyboard" />
              </button>
            )}
            <button
              type="button"
              className="btn icon-btn home-side"
              aria-label="Go home"
              data-tip="Go home"
              onClick={() => {
                // Stop first so the save is written before the page unloads.
                Promise.resolve(emu.stop()).finally(() => window.location.assign("/"));
              }}
            >
              <img src="/icons/favicon-64.png" alt="" className="icon" />
            </button>
            {/* The settings drawer: one labelled row per setting. The Game
                and Saves widgets render into the dock as its first rows. */}
            <Popup title="Settings" className="controls" open={controlsOpen} onClose={() => setControlsOpen(false)}>
              <div className="dock" ref={setDock} />
              <div className="field">
                <span className="field-label">Speed</span>
                <div className="field-control">
                  <Dropdown
                    value={String(emu.fastForward)}
                    options={SPEED_OPTIONS}
                    onChange={(v) => emu.setFastForward(Number(v))}
                    label="Fast-forward speed"
                  />
                  <label className="controls-check">
                    <input type="checkbox" checked={emu.fastForwardOn} onChange={() => emu.toggleFastForward()} />
                    <span className="muted small">Fast-forward on</span>
                  </label>
                </div>
              </div>
              <div className="field">
                <span className="field-label">Sound</span>
                <div className="field-control">
                  <label className="controls-check">
                    <input type="checkbox" checked={!emu.muted} onChange={() => emu.toggleMute()} />
                    <span className="muted small">On</span>
                  </label>
                </div>
              </div>
              <div className="field">
                <span className="field-label">Theme</span>
                <div className="field-control">
                  <ThemeControl />
                </div>
              </div>
              {touchOnly && emu.engine === "mgba" && (
                <div className="field">
                  <span className="field-label">Moving</span>
                  <div className="field-control">
                    <Dropdown value={movement} options={MOVEMENT_OPTIONS} onChange={setMovement} label="On-screen movement" />
                    <label className="controls-check">
                      <input type="checkbox" checked={shoulders} onChange={(e) => setShoulders(e.target.checked)} />
                      <span className="muted small">L and R ({cartridge === "gb" ? "Game Boy games" : "Advance games"})</span>
                    </label>
                  </div>
                </div>
              )}
              {touchOnly && onEjs && emu.current && (
                <>
                  <div className="field">
                    <span className="field-label">Stick</span>
                    <div className="field-control">
                      <Dropdown value={n64.stick} options={N64_MOVE_OPTIONS} onChange={(stick) => setN64({ stick })} label="Analog stick, this game" />
                      <span className="muted small">This game</span>
                    </div>
                  </div>
                  <div className="field">
                    <span className="field-label">D-pad</span>
                    <div className="field-control">
                      <Dropdown value={n64.dpad} options={N64_MOVE_OPTIONS} onChange={(dpad) => setN64({ dpad })} label="D-pad, this game" />
                      <span className="muted small">This game</span>
                    </div>
                  </div>
                </>
              )}
              {touchOnly && (document.fullscreenEnabled || !standalone) && (
                <div className="field">
                  <span className="field-label">Screen</span>
                  <div className="field-control">
                    <button
                      type="button"
                      className="btn"
                      aria-pressed={fullscreen}
                      onClick={document.fullscreenEnabled ? toggleFullscreen : () => setHomeScreenTip(true)}
                    >
                      <MaterialIcon name={fullscreen ? "fullscreen_exit" : "fullscreen"} />
                      {fullscreen ? "Exit fullscreen" : "Fullscreen landscape"}
                    </button>
                  </div>
                </div>
              )}
              {onEjs && (
                <div className="field">
                  <span className="field-label">Emulator</span>
                  <div className="field-control">
                    <label className="controls-check">
                      <input type="checkbox" checked={ejsMenuPinned} onChange={(e) => setEjsMenuPinned(e.target.checked)} />
                      <span className="muted small">Menu bar (or rest the mouse on the bottom of the game)</span>
                    </label>
                  </div>
                </div>
              )}
              {emu.cheats.length > 0 && (
                <div className="field">
                  <span className="field-label">Cheats</span>
                  <div className="field-control column">
                    {emu.cheats.map((cheat) => (
                      <label key={cheat.id} className="controls-check cheat-row">
                        <input
                          type="checkbox"
                          checked={emu.cheatChoice.includes(cheat.id)}
                          disabled={applyingCheats}
                          onChange={(e) => applyCheats(e.target.checked ? [...emu.cheatChoice, cheat.id] : emu.cheatChoice.filter((id) => id !== cheat.id))}
                        />
                        <span className="small">{cheat.name}</span>
                        <span className="muted small">{cheat.effect}</span>
                      </label>
                    ))}
                    <p className="muted small">Takes effect right away; the game keeps its place. Turn a cheat off once you have what you wanted.</p>
                  </div>
                </div>
              )}
            </Popup>
            {!touchOnly && keysOpen && (
              <Popup title="Keys" className="controls keyboard" onClose={() => setKeysOpen(false)}>
                <KeyHelp system={onEjs ? "n64" : (emu.cartridge ?? "gba")} game={emu.current} />
              </Popup>
            )}
            </>
          )}

          {/* Only under a running mGBA game: EmulatorJS draws its own, and
              while either engine opens a game there is nothing to press. */}
          {emu.engine === "mgba" && (
            <Touchpad
              core={emu.core}
              active={!!emu.current && !emu.paused}
              movement={movement}
              shoulders={shoulders}
              turboLocked={emu.fastForwardOn}
              onTurbo={emu.holdFastForward}
              onTurboLock={emu.toggleFastForward}
            />
          )}

        </div>

        <aside ref={guideSide}>
          {/* Only as pills under a running game: before that there is nothing
              to pick (games are chosen on the home page) or save. */}
          {emu.current && <Library emu={emu} dock={dock} />}
          {emu.current && <SavePanel emu={emu} dock={dock} />}
          {guideOpen && <GuidePanel core={emu.core} currentRom={onEjs ? null : emu.current} onHide={() => setGuideOpen(false)} />}
        </aside>

        {/* Under the pad and the guide in portrait on a phone (index.css). */}
        {emu.current && <GameCard name={emu.current} system={onEjs ? emu.ejsSystem : emu.cartridge} />}
      </main>
      {homeScreenTip && (
        <div className="modal-overlay" onClick={() => setHomeScreenTip(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Full screen on iPhone</h2>
              <button className="btn icon-btn" onClick={() => setHomeScreenTip(false)} aria-label="Close">
                <MaterialIcon name="close" />
              </button>
            </div>
            <p>iPhone browsers only go full screen for videos. Add CloudBoy to your home screen instead: it opens in its own window, with no address bar.</p>
            <ol className="about-list">
              <li>
                <strong>Safari</strong>: tap Share, then <strong>Add to Home Screen</strong>.
              </li>
              <li>
                <strong>Chrome</strong>: tap Share in the address bar, then <strong>Add to Home Screen</strong>.
              </li>
              <li>Open CloudBoy from the new icon.</li>
            </ol>
            <p className="muted small">
              The home-screen app keeps its own storage, so add your games and sign in there once. Cloud saves and the cloud
              library carry over.
            </p>
          </div>
        </div>
      )}
      {showLibraryModal && (
        <div className="modal-overlay" onClick={() => setShowLibraryModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Select Game</h2>
              <button
                className="btn icon-btn"
                onClick={() => setShowLibraryModal(false)}
                aria-label="Close"
              >
                <MaterialIcon name="close" />
              </button>
            </div>
            <div className="modal-body">
              <Library emu={emu} dock={null} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
